import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const update = (id, field, value) =>
    setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [field]: value } : p)));

  const save = async (p) => {
    setSaving(p.id);
    try {
      await api.put(`/admin/products/${p.id}`, {
        name: p.name,
        tagline: p.tagline,
        price: parseFloat(p.price),
        description: p.description,
      });
      toast.success(`${p.short_name} updated.`);
    } catch {
      toast.error("Update failed.");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>;

  return (
    <div>
      <h1 className="font-heading font-black text-3xl tracking-tight">Products</h1>
      <p className="text-muted-foreground mt-2">Edit pricing and copy for each tier.</p>

      <div className="space-y-5 mt-8">
        {products.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white border border-border p-6">
            <div className="flex items-center justify-between mb-5">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-primary">Tier {p.tier_level} · {p.short_name}</span>
              <button onClick={() => save(p)} disabled={saving === p.id} data-testid={`admin-product-save-${p.id}`}
                className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-sm font-semibold btn-lift disabled:opacity-60">
                {saving === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-2">Name</label>
                <input value={p.name} onChange={(e) => update(p.id, "name", e.target.value)} data-testid={`admin-product-name-${p.id}`}
                  className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Price (USD)</label>
                <input type="number" step="0.01" value={p.price} onChange={(e) => update(p.id, "price", e.target.value)} data-testid={`admin-product-price-${p.id}`}
                  className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium block mb-2">Tagline</label>
              <input value={p.tagline} onChange={(e) => update(p.id, "tagline", e.target.value)} data-testid={`admin-product-tagline-${p.id}`}
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium block mb-2">Description</label>
              <textarea rows={3} value={p.description} onChange={(e) => update(p.id, "description", e.target.value)} data-testid={`admin-product-desc-${p.id}`}
                className="w-full border border-border px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
